import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getProducts, getBundles } from "@/lib/api";
import { toast } from "sonner";

export const MAX_PER_ITEM = 5;

const CartContext = createContext(null);
const STORAGE_KEY = "pepsworld_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    Promise.all([getProducts(), getBundles()])
      .then(([products, bundles]) => {
        const prices = {};
        products.forEach((p) => (prices[`product:${p.id}`] = p.price));
        const bundleIds = {};
        bundles.forEach((b) => { prices[`bundle:${b.id}`] = b.price; bundleIds[`bundle:${b.id}`] = b.item_ids; });
        setItems((prev) => prev.map((i) => ({ ...i, qty: Math.min(MAX_PER_ITEM, i.qty), ...(prices[i.key] != null ? { price: prices[i.key] } : {}), ...(bundleIds[i.key] ? { item_ids: bundleIds[i.key] } : {}) })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const penIds = (i) => (i.type === "bundle" ? i.item_ids || [] : [i.id]);
  const roomFor = (ids, list = items) => {
    const units = {};
    list.forEach((i) => penIds(i).forEach((id) => (units[id] = (units[id] || 0) + i.qty)));
    return Math.min(...ids.map((id) => MAX_PER_ITEM - (units[id] || 0)));
  };
  const limitToast = () => toast.error(`Limit of ${MAX_PER_ITEM} per pen per order`, { description: "The Research Trio counts as 1 of each pen. This keeps it fair for everyone." });

  const addItem = (product, type = "product", qty = 1) => {
    const key = `${type}:${product.id}`;
    const ids = type === "bundle" ? product.item_ids || [] : [product.id];
    const allowed = Math.max(0, Math.min(qty, roomFor(ids)));
    if (allowed < qty) limitToast();
    if (allowed === 0) return 0;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + allowed } : i));
      }
      return [
        ...prev,
        { key, id: product.id, type, name: product.name, price: product.price, image: product.image, item_ids: type === "bundle" ? ids : undefined, qty: allowed },
      ];
    });
    return allowed;
  };

  const canIncrease = (key) => {
    const item = items.find((i) => i.key === key);
    return !!item && roomFor(penIds(item)) > 0;
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const updateQty = (key, qty) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, qty: Math.max(1, Math.min(qty, i.qty + roomFor(penIds(i), prev))) } : i))
        .filter((i) => i.qty > 0)
    );
  const clearCart = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.qty, 0),
    [items]
  );

  const value = {
    items,
    addItem,
    removeItem,
    updateQty,
    canIncrease,
    clearCart,
    count,
    subtotal,
    open,
    setOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
